import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircleIcon, ClockIcon, StarIcon } from '@heroicons/react/24/solid';
import { ArrowRightIcon, MapPinIcon, PhoneIcon, TagIcon } from '@heroicons/react/24/outline';
import type { ServiceProviderPricing } from '../../types/firestore';
import { Badge } from '../common';
import ServiceProviderImage from '../shared/ServiceProviderImage';
import {
  formatServiceHourlyRateLabel,
  getPublicServiceHourlyRate,
} from '../../utils/servicePricing';
import { getServiceProviderProfilePath } from '../../utils/serviceProviderProfilePath';

export interface ServiceCardProps {
  id: string;
  name: string;
  description?: string;
  address: string;
  specializations: string[];
  rating?: number;
  reviewCount?: number;
  availabilityStatus: {
    state: 'open' | 'closed' | 'unknown' | 'temporarily_unavailable';
    summaryText: string;
  };
  distance?: number;
  imageUrl?: string;
  imageStoragePath?: string;
  phoneNumber?: string;
  verifiedBusiness?: boolean;
  isTestData?: boolean;
  servicePricing?: ServiceProviderPricing | null;
  matchedSpecialization?: string | null;
  matchReason?: string | null;
  className?: string;
}

const getAvailabilityToneClassName = (state: ServiceCardProps['availabilityStatus']['state']) => {
  if (state === 'open') {
    return 'text-status-success-400';
  }

  if (state === 'temporarily_unavailable') {
    return 'text-status-warning-400';
  }

  return 'text-[rgb(var(--rs-color-text-secondary))]';
};

const formatDistance = (distance: number): string =>
  distance < 1 ? `${(distance * 1000).toFixed(0)} m` : `${distance.toFixed(1)} km`;

const ServiceCard: React.FC<ServiceCardProps> = ({
  id,
  name,
  description,
  address,
  specializations,
  rating,
  reviewCount = 0,
  availabilityStatus,
  distance,
  imageUrl,
  imageStoragePath,
  phoneNumber,
  verifiedBusiness = false,
  isTestData = false,
  servicePricing,
  matchedSpecialization,
  matchReason,
  className = '',
}) => {
  const publicHourlyRate = getPublicServiceHourlyRate({ servicePricing });
  const hourlyRateLabel = publicHourlyRate
    ? formatServiceHourlyRateLabel(publicHourlyRate.amount)
    : null;
  const safeReviewCount = Number.isFinite(reviewCount) ? Math.max(0, reviewCount) : 0;
  const safeRating = typeof rating === 'number' && Number.isFinite(rating) ? rating : 0;
  const normalizedSpecializations = specializations
    .map((specialization) => specialization.trim())
    .filter(Boolean);
  const visibleSpecializations = normalizedSpecializations.slice(0, 2);
  const hiddenSpecializationsCount = Math.max(
    normalizedSpecializations.length - visibleSpecializations.length,
    0
  );
  const specializationsSummary =
    visibleSpecializations.length > 0
      ? `${visibleSpecializations.join(', ')}${
          hiddenSpecializationsCount > 0 ? ` +${hiddenSpecializationsCount}` : ''
        }`
      : 'Zakres ustalany indywidualnie';
  const hasDescription = Boolean(description?.trim());
  const hasPhoneNumber = Boolean(phoneNumber?.trim());

  return (
    <article
      className={`rs-surface-card grid h-full min-w-0 grid-cols-[5.75rem_minmax(0,1fr)] overflow-hidden sm:grid-cols-[7rem_minmax(0,1fr)] lg:grid-cols-[8rem_minmax(0,1fr)] ${className}`}
      data-testid="service-card"
    >
      <div className="rs-surface-workbench relative min-h-full overflow-hidden">
        <ServiceProviderImage
          src={imageUrl}
          storagePath={imageStoragePath}
          alt={name}
          isTestData={isTestData}
          className="h-full min-h-44 w-full object-cover"
          diagnosticContext={{
            component: 'ServiceCard',
            providerId: id,
            variant: 'thumb',
          }}
        />
      </div>

      <div className="flex min-w-0 flex-col p-3 sm:p-3.5">
        <div className="flex min-w-0 items-start gap-2">
          <h3
            className="min-w-0 flex-1 overflow-hidden text-base font-semibold leading-snug text-[rgb(var(--rs-color-text-primary))] [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]"
            title={name}
          >
            {name}
          </h3>
          {verifiedBusiness && (
            <span
              className="mt-0.5 shrink-0 text-[rgb(var(--rs-color-repair-green))]"
              aria-label="Zweryfikowana firma"
              title="Zweryfikowana firma"
            >
              <CheckCircleIcon className="h-5 w-5" aria-hidden="true" />
            </span>
          )}
        </div>

        <div className="mt-2 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          <span
            className={`inline-flex min-w-0 items-center gap-1.5 font-medium ${getAvailabilityToneClassName(
              availabilityStatus.state
            )}`}
            data-testid="service-card-availability"
          >
            <ClockIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="line-clamp-1">{availabilityStatus.summaryText}</span>
          </span>

          {safeReviewCount > 0 ? (
            <span
              className="inline-flex items-center gap-1 text-[rgb(var(--rs-color-text-secondary))]"
              aria-label={`Ocena ${safeRating.toFixed(1)} na 5, ${safeReviewCount} opinii`}
              data-testid="service-card-rating"
            >
              <StarIcon
                className="h-3.5 w-3.5 text-[rgb(var(--rs-color-accent-gold))]"
                aria-hidden="true"
              />
              <span className="font-semibold text-[rgb(var(--rs-color-text-primary))]">
                {safeRating.toFixed(1)}
              </span>
              <span>({safeReviewCount})</span>
            </span>
          ) : (
            <span className="italic text-[rgb(var(--rs-color-text-muted))]">Brak opinii</span>
          )}
        </div>

        {isTestData && (
          <div className="mt-2">
            <Badge variant="neutral" size="sm">
              Profil testowy
            </Badge>
          </div>
        )}

        <div className="mt-2.5 space-y-1.5 text-sm text-[rgb(var(--rs-color-text-secondary))]">
          <div className="flex min-w-0 items-start gap-1.5">
            <MapPinIcon
              className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--rs-color-text-muted))]"
              aria-hidden="true"
            />
            <p className="min-w-0 line-clamp-2" title={address}>
              {address}
              {distance !== undefined && Number.isFinite(distance) && (
                <span className="text-[rgb(var(--rs-color-text-muted))]">
                  {' '}
                  · {formatDistance(distance)}
                </span>
              )}
            </p>
          </div>

          {hasDescription && (
            <p className="line-clamp-2 text-xs leading-relaxed text-[rgb(var(--rs-color-text-muted))]">
              {description?.trim()}
            </p>
          )}
        </div>

        <div className="mt-2.5 space-y-1.5 text-xs text-[rgb(var(--rs-color-text-secondary))]">
          {matchReason && (
            <p
              className="line-clamp-2 font-medium text-[rgb(var(--rs-color-text-primary))]"
              data-testid="service-card-match-reason"
            >
              Pasuje: {matchReason}
            </p>
          )}

          <div className="flex min-w-0 items-start gap-1.5">
            <TagIcon
              className="mt-px h-3.5 w-3.5 shrink-0 text-[rgb(var(--rs-color-text-muted))]"
              aria-hidden="true"
            />
            <p className="min-w-0 line-clamp-2" title={specializationsSummary}>
              {matchedSpecialization ? (
                <>
                  <span className="font-medium text-[rgb(var(--rs-color-text-primary))]">
                    Najlepsze dopasowanie:
                  </span>{' '}
                  {matchedSpecialization}
                </>
              ) : (
                specializationsSummary
              )}
            </p>
          </div>
        </div>

        <div className="mt-auto pt-3">
          <div className="mb-2 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[rgb(var(--rs-color-text-muted))]">
            {hourlyRateLabel && (
              <span
                className="font-semibold text-[rgb(var(--rs-color-text-secondary))]"
                data-testid="service-card-hourly-rate"
              >
                {hourlyRateLabel}
              </span>
            )}
            {hasPhoneNumber && (
              <span className="inline-flex min-w-0 items-center gap-1.5">
                <PhoneIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span className="truncate" title={phoneNumber}>
                  {phoneNumber?.trim()}
                </span>
              </span>
            )}
          </div>

          <Link
            to={getServiceProviderProfilePath(id)}
            className="focus-outline rs-action-primary rs-action-md min-h-11 w-full active:opacity-90"
          >
            Profil serwisu
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </article>
  );
};

export default ServiceCard;
